import { CharSet } from "refa";
import { getMatchingDirectionFromAssertionKind, isStrictBackreference, getLengthRange, hasSomeDescendant, isEmptyBackreference, invertMatchingDirection, } from "./basic";
import { toUnicodeSet } from "./to-char-set";
import { followPaths } from "./follow";
import { assertNever, CharUnion, intersectInexact, isReadonlyArray, unionInexact } from "./util";
import { Chars } from "./chars";
import { CacheInstance } from "./cache";
/**
 * This namespace contains methods for working with {@link FirstLookChar}s.
 */
// eslint-disable-next-line @typescript-eslint/no-namespace
export var FirstLookChars;
(function (FirstLookChars) {
    /**
     * Returns a {@link FirstLookChar} that is equivalent to a trivially accepting lookaround.
     *
     * The returned look is semantically equivalent to `(?=)` == `(?=[^]|$)` or `(?<=)` == `(?<=[^]|^)`.
     */
    function all(flags) {
        return {
            char: Chars.all(flags),
            exact: true,
            edge: true,
        };
    }
    FirstLookChars.all = all;
    /**
     * Returns a {@link FirstLookChar} that is equivalent to an assertion that only accepts the start/end of the input
     * string.
     *
     * The returned look is semantically equivalent to `$` == `(?=[]|$)` or `^` == `(?<=[]|^)`.
     */
    function edge(flags) {
        return {
            char: Chars.empty(flags),
            exact: true,
            edge: true,
        };
    }
    FirstLookChars.edge = edge;
    /**
     * Converts the given {@link FirstLookChar} to a {@link FirstConsumedChar}.
     *
     * This is semantically equivalent to `(?=b|$)` -> `[]|(?=b|$)`.
     *
     * Note: This operation will typically return a {@link FirstPartiallyConsumedChar}. It will only return a
     * {@link FirstFullyConsumedChar} if the given `char` is empty and `edge: false`. This is because
     * `(?=[])` -> `[]|(?=[])` == `[]`.
     */
    function toConsumed(look) {
        if (!look.edge && look.char.isEmpty) {
            // the given look trivially rejects everything
            return {
                char: CharSet.empty(look.char.maximum),
                exact: true,
                empty: false,
            };
        }
        else {
            return {
                char: CharSet.empty(look.char.maximum),
                exact: true,
                empty: true,
                look,
            };
        }
    }
    FirstLookChars.toConsumed = toConsumed;
})(FirstLookChars || (FirstLookChars = {}));
/**
 * This namespace contains methods for working with {@link FirstConsumedChar}s.
 */
// eslint-disable-next-line @typescript-eslint/no-namespace
export var FirstConsumedChars;
(function (FirstConsumedChars) {
    /**
     * Returns a {@link FirstConsumedChar} that is equivalent to the empty concatenation.
     */
    function emptyConcat(flags) {
        return {
            char: Chars.empty(flags),
            exact: true,
            empty: true,
            look: FirstLookChars.all(flags),
        };
    }
    FirstConsumedChars.emptyConcat = emptyConcat;
    /**
     * Returns a {@link FirstConsumedChar} that is equivalent to the empty union (or empty set).
     */
    function emptyUnion(flags) {
        return {
            char: Chars.empty(flags),
            exact: true,
            empty: false,
        };
    }
    FirstConsumedChars.emptyUnion = emptyUnion;
    /**
     * Converts the given {@link FirstConsumedChar} to a {@link FirstLookChar}.
     *
     * This is conceptually equivalent to wrapping the given consumed character into a lookaround.
     *
     * This is semantically equivalent to `a|(?=b|$)` -> `(?=a|(?=b|$))` == `(?=[ab]|$)`.
     */
    function toLook(consumed) {
        if (consumed.empty) {
            // We have 2 cases:
            //   (1) (?=a|(?=b))
            //       (?=a|b)
            //       (?=[ab])
            //   (2) (?=a|(?=b|$))
            //       (?=a|b|$)
            //       (?=[ab]|$)
            const union = unionInexact(consumed, consumed.look);
            return {
                char: union.char,
                exact: union.exact,
                edge: consumed.look.edge,
            };
        }
        else {
            // It's already in the correct form:
            //   (?=a)
            return {
                char: consumed.char,
                exact: consumed.exact,
                edge: false,
            };
        }
    }
    FirstConsumedChars.toLook = toLook;
    /**
     * Creates the union of all the given {@link FirstConsumedChar}s.
     *
     * The result is independent of the order in which the characters are given.
     */
    function union(chars, flags) {
        const union = CharUnion.fromFlags(flags);
        const looks = [];
        for (const itemChar of chars) {
            union.add(itemChar);
            if (itemChar.empty) {
                looks.push(itemChar.look);
            }
        }
        if (looks.length > 0) {
            if (looks.length === 1) {
                return {
                    char: union.char,
                    exact: union.exact,
                    empty: true,
                    look: looks[0],
                };
            }
            // This means that the unioned elements look something like this:
            //   (a|(?=g)|b?|x)
            //
            // Adding the trivially accepting look after all all alternatives that can be empty, we'll get:
            //   (a|(?=g)|b?|x)
            //   (a|(?=g)|b?(?=[^]|$)|x)
            //   (a|(?=g)|b(?=[^]|$)|(?=[^]|$)|x)
            //
            // Since we are only interested in the first character, the look in `b(?=[^]|$)` can be removed.
            //   (a|(?=g)|b|(?=[^]|$)|x)
            //   (a|b|x|(?=g)|(?=[^]|$))
            //   ([abx]|(?=g)|(?=[^]|$))
            //
            // To union the looks, we can simply use the fact that `(?=a)|(?=b)` == `(?=a|b)`
            //   ([abx]|(?=g)|(?=[^]|$))
            //   ([abx]|(?=g|[^]|$))
            //   ([abx]|(?=[^]|$))
            //
            // And with that we are done. This is exactly the form of a first partial char. Getting the exactness of the
            // union of normal chars and look chars follows the same rules.
            const lookUnion = CharUnion.fromFlags(flags);
            let edge = false;
            for (const look of looks) {
                lookUnion.add(look);
                edge = edge || look.edge;
            }
            return {
                char: union.char,
                exact: union.exact,
                empty: true,
                look: { char: lookUnion.char, exact: lookUnion.exact, edge },
            };
        }
        else {
            return { char: union.char, exact: union.exact, empty: false };
        }
    }
    FirstConsumedChars.union = union;
    /**
     * Creates the concatenation of all the given {@link FirstConsumedChar}s.
     *
     * The given char iterable is evaluated **lazily**. The implementation will try to iterate as few chars as possible.
     */
    function concat(chars, flags) {
        const union = CharUnion.fromFlags(flags);
        let look = FirstLookChars.all(flags);
        for (const item of chars) {
            union.add(intersectInexact(item, look));
            if (item.empty) {
                // This is the hard case. We need to convert the expression
                //   (a|(?=b))(c|(?=d))
                // into an expression
                //   e|(?=f)
                // (we will completely ignore edge assertions for now)
                //
                // To do that, we'll use the following idea:
                //   (a|(?=b))(c|(?=d))
                //   a(c|(?=d))|(?=b)(c|(?=d))
                //   ac|a(?=d)|(?=b)c|(?=b)(?=d)
                //
                // Since we are only interested in the first char, we can remove the `c` in `ac` and the `(?=d)` in
                // `a(?=d)`. Furthermore, `(?=b)c` is a single char, so let's call it `C` for now.
                //   ac|a(?=d)|(?=b)c|(?=b)(?=d)
                //   a|a|C|(?=b)(?=d)
                //   [aC]|(?=b)(?=d)
                //   [aC]|(?=(?=b)d)
                //
                // This is *almost* the desired form. We now have to convert `(?=(?=b)d)` to an expression of the form
                // `(?=f)`. This is the point where we can't ignore edge assertions any longer. Let's look at all possible
                // cases and see how it plays out. Also, let `D` be the char intersection of `b` and `d`.
                //   (1) (?=(?=b)d)
                //       (?=D)
                //
                //   (2) (?=(?=b)(d|$))
                //       (?=(?=b)d|(?=b)$)
                //       (?=D)
                //
                //   (3) (?=(?=b|$)d)
                //       (?=((?=b)|$)d)
                //       (?=(?=b)d|$d)
                //       (?=D)
                //
                //   (4) (?=(?=b|$)(d|$))
                //       (?=((?=b)|$)(d|$))
                //       (?=(?=b)(d|$)|$(d|$))
                //       (?=(?=b)d|(?=b)$|$d|$$)
                //       (?=D|$)
                //
                // As we can see, the look char is always `D` and the edge is only accepted if it's accepted by both.
                const lookIntersection = intersectInexact(look, item.look);
                look = {
                    char: lookIntersection.char,
                    exact: lookIntersection.exact,
                    edge: look.edge && item.look.edge,
                };
                if (!look.edge && look.char.isEmpty) {
                    // The look trivially rejects everything
                    return { char: union.char, exact: union.exact, empty: false };
                }
            }
            else {
                return { char: union.char, exact: union.exact, empty: false };
            }
        }
        return { char: union.char, exact: union.exact, empty: true, look };
    }
    FirstConsumedChars.concat = concat;
    /**
     * Makes the given consumed character optional.
     *
     * This is semantically equivalent to `a|(?=b|$)` -> `a?`.
     */
    function makeOptional(consumed) {
        return {
            char: consumed.char,
            exact: consumed.exact,
            empty: true,
            look: { char: CharSet.all(consumed.char.maximum), exact: true, edge: true },
        };
    }
    FirstConsumedChars.makeOptional = makeOptional;
})(FirstConsumedChars || (FirstConsumedChars = {}));
class ImplOptions {
    constructor(flags) {
        this._currentWordBoundaries = [];
        // We need a cache to avoid an exponential worst case regarding boundary assertions.
        // If the current flags are a cache instance, we'll use the cache from there and if not, then we'll create a
        // new cache.
        if (flags instanceof CacheInstance) {
            this._ltrCache = flags.getFirstConsumedCharLTR;
            this._rtlCache = flags.getFirstConsumedCharRTL;
        }
        else {
            this._ltrCache = new WeakMap();
            this._rtlCache = new WeakMap();
        }
    }
    isCurrentWordBoundary(element) {
        return this._currentWordBoundaries.some(e => e === element);
    }
    pushWordBoundary(element) {
        this._currentWordBoundaries.push(element);
    }
    popWordBoundary() {
        this._currentWordBoundaries.pop();
    }
    getCached(element, dir) {
        if (dir === "ltr") {
            return this._ltrCache.get(element);
        }
        else {
            return this._rtlCache.get(element);
        }
    }
    setCached(element, dir, result) {
        if (dir === "ltr") {
            this._ltrCache.set(element, result);
        }
        else {
            this._rtlCache.set(element, result);
        }
    }
}
/**
 * If a character is returned, it guaranteed to be a super set of the actual character. If the given element is
 * always of zero length, then the empty character set will be returned.
 *
 * If `exact` is `true` then it is guaranteed that the returned character is guaranteed to be the actual
 * character at all times if this element is not influenced by lookarounds outside itself.
 *
 * ## Partially consumed
 *
 * Only the given element and its children are processed. This is important when considering partially consumed first
 * characters. The lookaround is derived only from the assertions inside the given element.
 *
 * E.g. In `/b?a/`, the result for `b?` is `{ char: 'b', empty: true, look: { char: all, edge: true } }`. The
 * lookaround accepts all characters because it doesn't take the `a` after `b?` into consideration.
 */
export function getFirstConsumedChar(element, direction, flags) {
    const options = new ImplOptions(flags);
    if (isReadonlyArray(element)) {
        return getFirstConsumedCharAlternativesImpl(element, direction, flags, options);
    }
    else {
        return getFirstConsumedCharImpl(element, direction, flags, options);
    }
}
function getFirstConsumedCharAlternativesImpl(alternatives, direction, flags, options) {
    return FirstConsumedChars.union(alternatives.map(e => getFirstConsumedCharImpl(e, direction, flags, options)), flags);
}
function getFirstConsumedCharImpl(element, direction, flags, options) {
    let result = options.getCached(element, direction);
    if (result === undefined) {
        result = getFirstConsumedCharUncachedImpl(element, direction, flags, options);
        options.setCached(element, direction, result);
    }
    return result;
}
function getFirstConsumedCharAssertionImpl(element, direction, flags, options) {
    switch (element.kind) {
        case "word":
            if (options.isCurrentWordBoundary(element)) {
                // this means that the value of a word boundary assertion depends on itself indirectly.
                // we have to stop the recursion here because infinite recursion is possible otherwise.
                return misdirectedAssertion();
            }
            else {
                options.pushWordBoundary(element);
                const before = getFirstCharAfterImpl(element, invertMatchingDirection(direction), flags, options);
                options.popWordBoundary();
                // Remember:
                //   \B == (?<=\w)(?=\w)|(?<!\w)(?!\w)
                //   \b == (?<!\w)(?=\w)|(?<=\w)(?!\w)
                const word = Chars.word(flags);
                if (before.edge) {
                    // this forces our hand a little. Since the previous "character" might be the start/end of
                    // the string, we have to enter the alternative that starts with `(?<!\w)`
                    if (before.char.isDisjointWith(word)) {
                        return wordAssertion(element.negate);
                    }
                    else {
                        // it might be either of the alternatives
                        return misdirectedAssertion();
                    }
                }
                else {
                    if (before.char.isDisjointWith(word)) {
                        return wordAssertion(element.negate);
                    }
                    else if (before.char.isSubsetOf(word)) {
                        return wordAssertion(!element.negate);
                    }
                    else {
                        // it might be either of the alternatives
                        return misdirectedAssertion();
                    }
                }
            }
        case "end":
        case "start":
            if (getMatchingDirectionFromAssertionKind(element.kind) === direction) {
                if (flags.multiline) {
                    return lineAssertion();
                }
                else {
                    return edgeAssertion();
                }
            }
            else {
                return misdirectedAssertion();
            }
        case "lookahead":
        case "lookbehind":
            if (getMatchingDirectionFromAssertionKind(element.kind) === direction) {
                if (element.negate) {
                    // A little note about negative:
                    //
                    // Negation is hard because it throws the idea of exactness on its heads. The interface defines
                    // exactness in a way that means: "we only guarantee that the returned characters are a superset of
                    // the actual (=correct) characters." Negation is incompatible with that definition of exactness
                    // because negating a _superset_ means that we can only guarantee a _subset_. So we can only do
                    // _exact_ negation. This is a big limitation.
                    //
                    // So what negations can be done _exactly_?
                    // Single-character negations, e.g. `(?!a)` or `(?!a|b|\d)`. That's it. All other negated assertions
                    // are not doable _in general_.
                    if (hasSomeDescendant(element, d => d !== element && d.type === "Assertion")) {
                        return misdirectedAssertion();
                    }
                    const firstChar = getFirstConsumedCharAlternativesImpl(element.alternatives, direction, flags, options);
                    const range = getLengthRange(element.alternatives, flags);
                    if (firstChar.empty || !range) {
                        // trivially rejecting
                        return { char: Chars.empty(flags), empty: false, exact: true };
                    }
                    if (!firstChar.exact || range.max !== 1) {
                        // the goal to to convert `(?![a])` to `(?=[^a]|$)` but this negation is only correct
                        // if the characters are exact and if the assertion asserts at most one character
                        // E.g. `(?![a][b])` == `(?=$|[^a]|[a][^b])`
                        return misdirectedAssertion();
                    }
                    else {
                        return FirstLookChars.toConsumed({ char: firstChar.char.negate(), edge: true, exact: true });
                    }
                }
                else {
                    const firstChar = getFirstConsumedCharAlternativesImpl(element.alternatives, direction, flags, options);
                    return FirstLookChars.toConsumed(FirstConsumedChars.toLook(firstChar));
                }
            }
            else {
                return misdirectedAssertion();
            }
        default:
            throw assertNever(element);
    }
    /**
     * The result for an assertion that (partly) assert for the wrong matching direction.
     */
    function misdirectedAssertion() {
        return FirstLookChars.toConsumed({
            char: Chars.all(flags),
            edge: true,
            // This is the important part.
            // Since the allowed chars depend on the previous chars, we don't know which will be allowed.
            exact: false,
        });
    }
    function edgeAssertion() {
        return FirstLookChars.toConsumed(FirstLookChars.edge(flags));
    }
    function lineAssertion() {
        return FirstLookChars.toConsumed({
            char: Chars.lineTerminator(flags),
            edge: true,
            exact: true,
        });
    }
    function wordAssertion(negate) {
        const word = Chars.word(flags);
        return FirstLookChars.toConsumed({
            char: negate ? word.negate() : word,
            edge: negate,
            exact: true,
        });
    }
}
function getFirstConsumedCharUncachedImpl(element, direction, flags, options) {
    switch (element.type) {
        case "Assertion":
            return getFirstConsumedCharAssertionImpl(element, direction, flags, options);
        case "Character":
        case "CharacterSet":
        case "CharacterClass":
        case "ExpressionCharacterClass": {
            const set = toUnicodeSet(element, flags);
            if (set.accept.isEmpty) {
                return { char: set.chars, empty: false, exact: true };
            }
            else {
                const firstChars = new Set();
                if (direction === "ltr") {
                    for (const wordSet of set.accept.wordSets) {
                        if (wordSet.length > 0) {
                            firstChars.add(wordSet[0]);
                        }
                    }
                }
                else {
                    for (const wordSet of set.accept.wordSets) {
                        if (wordSet.length > 0) {
                            firstChars.add(wordSet[wordSet.length - 1]);
                        }
                    }
                }
                const consumed = {
                    char: set.chars.union(...firstChars),
                    empty: false,
                    exact: true,
                };
                return set.hasEmptyWord ? FirstConsumedChars.makeOptional(consumed) : consumed;
            }
        }
        case "Quantifier": {
            if (element.max === 0) {
                return FirstConsumedChars.emptyConcat(flags);
            }
            const firstChar = getFirstConsumedCharImpl(element.element, direction, flags, options);
            if (element.min === 0) {
                return FirstConsumedChars.makeOptional(firstChar);
            }
            else {
                return firstChar;
            }
        }
        case "Alternative": {
            let elements = element.elements;
            if (direction === "rtl") {
                elements = [...elements];
                elements.reverse();
            }
            return FirstConsumedChars.concat((function* () {
                for (const e of elements) {
                    yield getFirstConsumedCharImpl(e, direction, flags, options);
                }
            })(), flags);
        }
        case "CapturingGroup":
        case "Group":
            return getFirstConsumedCharAlternativesImpl(element.alternatives, direction, flags, options);
        case "Backreference": {
            if (isEmptyBackreference(element, flags)) {
                return FirstConsumedChars.emptyConcat(flags);
            }
            let resolvedChar = getFirstConsumedCharImpl(element.resolved, direction, flags, options);
            // the resolved character is only exact if it is only a single character.
            // i.e. /(\w)\1/ here the (\w) will capture exactly any word character, but the \1 can only match
            // one word character and that is the only (\w) matched.
            if (resolvedChar.exact && resolvedChar.char.size > 1) {
                resolvedChar = Object.assign(Object.assign({}, resolvedChar), { exact: false });
            }
            if (isStrictBackreference(element)) {
                return resolvedChar;
            }
            else {
                // there is at least one path through which the backreference will (possibly) be replaced with the
                // empty string
                return FirstConsumedChars.makeOptional(resolvedChar);
            }
        }
        default:
            throw assertNever(element);
    }
}
export function getFirstConsumedCharAfter(afterThis, direction, flags) {
    return getFirstConsumedCharAfterImpl(afterThis, direction, flags, new ImplOptions(flags));
}
function getFirstConsumedCharAfterImpl(afterThis, direction, flags, options) {
    const result = followPaths(afterThis, "next", FirstConsumedChars.emptyConcat(flags), {
        join(states) {
            return FirstConsumedChars.union(states, flags);
        },
        enter(element, state, direction) {
            const first = getFirstConsumedCharImpl(element, direction, flags, options);
            return FirstConsumedChars.concat([state, first], flags);
        },
        continueInto() {
            return false;
        },
        continueAfter(_, state) {
            return state.empty;
        },
        continueOutside(element, _, direction) {
            return getMatchingDirectionFromAssertionKind(element.kind) !== direction;
        },
    }, direction);
    return result;
}
/**
 * Returns the first character after the given element.
 *
 * What "after" means depends the on the given direction which will be interpreted as the current matching
 * direction. You can use this to get the previous character of an element as well.
 */
export function getFirstCharAfter(afterThis, direction, flags) {
    return getFirstCharAfterImpl(afterThis, direction, flags, new ImplOptions(flags));
}
function getFirstCharAfterImpl(afterThis, direction, flags, options) {
    return FirstConsumedChars.toLook(getFirstConsumedCharAfterImpl(afterThis, direction, flags, options));
}
/**
 * This function behaves exactly like {@link getFirstConsumedCharAfter} but it also tracks what elements contribute to
 * the result.
 */
export function getFirstConsumedCharAfterWithContributors(afterThis, direction, flags) {
    return getFirstConsumedCharAfterWithContributorsImpl(afterThis, direction, flags, new ImplOptions(flags));
}
function getFirstConsumedCharAfterWithContributorsImpl(afterThis, direction, flags, option) {
    const result = followPaths(afterThis, "next", { char: FirstConsumedChars.emptyConcat(flags), contributors: [] }, {
        join(states) {
            const contributors = new Set();
            states.forEach(s => s.contributors.forEach(e => contributors.add(e)));
            return {
                char: FirstConsumedChars.union(states.map(s => s.char), flags),
                contributors: [...contributors],
            };
        },
        enter(element, state, direction) {
            const first = getFirstConsumedCharImpl(element, direction, flags, option);
            return {
                char: FirstConsumedChars.concat([state.char, first], flags),
                contributors: [...state.contributors, element],
            };
        },
        continueInto() {
            return false;
        },
        continueAfter(_, state) {
            return state.char.empty;
        },
        continueOutside(element, _, direction) {
            return getMatchingDirectionFromAssertionKind(element.kind) !== direction;
        },
    }, direction);
    return result;
}
/**
 * This function behaves exactly like {@link getFirstCharAfter} but it also tracks what elements contribute to the
 * result.
 */
export function getFirstCharAfterWithContributors(afterThis, direction, flags) {
    return getFirstCharAfterWithContributorsImpl(afterThis, direction, flags, new ImplOptions(flags));
}
function getFirstCharAfterWithContributorsImpl(afterThis, direction, flags, option) {
    const { char, contributors } = getFirstConsumedCharAfterWithContributorsImpl(afterThis, direction, flags, option);
    return { char: FirstConsumedChars.toLook(char), contributors };
}
